import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material'
import {
  Assessment,
  TrendingUp,
  People,
  Folder,
  VideoCall,
  Download,
  Description,
  EmojiEvents,
  Calculate,
  Campaign,
  CheckCircle,
  Cancel,
  Warning,
  Replay,
} from '@mui/icons-material'
import { useReopenScoreMutation } from '../store/api/scoringApi'
import {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
  useLazyGetRoundCompletionQuery,
  useLazyGetRankingQuery,
  usePublishRoundMutation,
  useSetR1ShortlistMutation,
  type RankingResult,
} from '../store/api/reportsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import {
  useLazyAdminDownloadTeamCertificateQuery,
  useLazyAdminDownloadAllCertificatesQuery,
} from '../store/api/achievementsApi'
import { useAppSelector } from '../hooks/redux'
import { useHasRole } from '../hooks/usePermission'
import { PageHeader } from '../components/PageHeader'
import { getCardSurfaceSx, getContentPaperSx, getTablePaperSx, getFilterPaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'
import { publishAutoPick } from '../utils/r2'

/**
 * Spec PHẦN 9 — Bảng xếp hạng R1 (dual_claimant_packets).
 *
 * 13 cột:
 *   1. # (rank)         2. Đội            3. ĐH/Trường
 *   4. Hồ sơ 1 — Vai trò    5. HS1 Đơn KK /50    6. HS1 Chứng cứ /50    7. HS1 Tổng /100
 *   8. Hồ sơ 2 — Vai trò    9. HS2 Đơn KK /50   10. HS2 Chứng cứ /50   11. HS2 Tổng /100
 *  12. Tổng /200      13. Video /30 (tie-break)
 *
 * Top 15 đội đầu (rank ≤ 15) được highlight nhẹ — gợi ý ngưỡng publish Bán kết.
 */
function R1DualRankingTable({
  ranking,
  teams,
}: {
  ranking: RankingResult
  teams: Array<{ id: string; competition_status?: string | null }>
}) {
  const roleLabel = (r?: string | null) =>
    r === 'plaintiff' ? 'Nguyên đơn' : r === 'defendant' ? 'Bị đơn' : '—'

  const subAvg = (judges: Array<{ memorial_score: number; evidence_score: number }> | undefined) => {
    if (!judges || judges.length === 0) return { mem: 0, evi: 0 }
    const mem = judges.reduce((a, j) => a + (j.memorial_score || 0), 0) / judges.length
    const evi = judges.reduce((a, j) => a + (j.evidence_score || 0), 0) / judges.length
    return { mem, evi }
  }

  return (
    <TableContainer sx={(t) => getTablePaperSx(t)}>
      <Table size="small" sx={{ minWidth: 1280 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 56 }}>Số thứ tự</TableCell>
            <TableCell sx={{ minWidth: 180 }}><strong>Đội</strong></TableCell>
            <TableCell sx={{ minWidth: 140 }}><strong>ĐH/Trường</strong></TableCell>
            <TableCell sx={{ minWidth: 110 }}>
              <strong>Hồ sơ 1</strong>
              <Typography variant="caption" color="text.secondary" display="block">Vai trò</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS1 Đơn KK</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS1 CC</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 90 }}>
              <strong>HS1 Tổng</strong>
              <Typography variant="caption" color="text.secondary" display="block">/100</Typography>
            </TableCell>
            <TableCell sx={{ minWidth: 110 }}>
              <strong>Hồ sơ 2</strong>
              <Typography variant="caption" color="text.secondary" display="block">Vai trò</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS2 Đơn KK</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS2 CC</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 90 }}>
              <strong>HS2 Tổng</strong>
              <Typography variant="caption" color="text.secondary" display="block">/100</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 100 }}>
              <strong>Tổng</strong>
              <Typography variant="caption" color="text.secondary" display="block">/200</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 110 }}>
              <strong>Video</strong>
              <Typography variant="caption" color="text.secondary" display="block">/30 (tie-break)</Typography>
            </TableCell>
            <TableCell align="center" sx={{ minWidth: 140 }}>
              <strong>Trạng thái</strong>
              <Typography variant="caption" color="text.secondary" display="block">sau công bố</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ranking.ranking.map((r) => {
            const sub1 = subAvg(r.submission_1_judges)
            const sub2 = subAvg(r.submission_2_judges)
            const inTop15 = r.rank <= 15
            return (
              <TableRow
                key={r.team_id}
                hover
                sx={{
                  verticalAlign: 'top',
                  ...(inTop15 && {
                    bgcolor: (t) => t.palette.action.hover,
                  }),
                }}
              >
                <TableCell>
                  <Chip
                    label={`#${r.rank}`}
                    size="small"
                    color={r.rank === 1 ? 'warning' : r.rank <= 4 ? 'primary' : inTop15 ? 'info' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.team_name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {r.university || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={roleLabel(r.submission_1_representing_role)}
                    color={r.submission_1_representing_role === 'plaintiff' ? 'primary' : r.submission_1_representing_role === 'defendant' ? 'secondary' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem' }}
                  />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                    {r.submission_1_judge_count || 0} GK
                  </Typography>
                </TableCell>
                <TableCell align="right">{sub1.mem.toFixed(1)}</TableCell>
                <TableCell align="right">{sub1.evi.toFixed(1)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {(r.submission_1_score ?? 0).toFixed(1)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={roleLabel(r.submission_2_representing_role)}
                    color={r.submission_2_representing_role === 'plaintiff' ? 'primary' : r.submission_2_representing_role === 'defendant' ? 'secondary' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem' }}
                  />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                    {r.submission_2_judge_count || 0} GK
                  </Typography>
                </TableCell>
                <TableCell align="right">{sub2.mem.toFixed(1)}</TableCell>
                <TableCell align="right">{sub2.evi.toFixed(1)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {(r.submission_2_score ?? 0).toFixed(1)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700} color="primary.main">
                    {(r.main_score ?? 0).toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    / {ranking.main_score_max ?? 200}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {(r.effective_video_score ?? 0) > 0 ? (
                    <Chip
                      size="small"
                      label={`Σ ${(r.effective_video_score ?? 0).toFixed(1)}`}
                      color={r.tied_with_neighbor ? 'warning' : 'default'}
                      variant={r.tied_with_neighbor ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      —
                    </Typography>
                  )}
                  {r.tied_with_neighbor && (
                    <Typography variant="caption" color="warning.main" display="block">
                      ⚖ Đồng điểm
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {(() => {
                    // Bug fix 2026-05-24: cột Trạng thái cho biết kết quả thực sau
                    // shortlist / publish, không chỉ rank gợi ý. Pull từ
                    // team.competition_status (BE set trong set_r1_shortlist + publish_round_results).
                    const team = teams.find((t) => t.id === r.team_id)
                    const cs = team?.competition_status
                    if (cs === 'advanced_to_semifinal') {
                      return <Chip size="small" label="Vào Bán kết" color="success" sx={{ fontWeight: 700 }} />
                    }
                    if (cs === 'advanced_to_final') {
                      return <Chip size="small" label="Vào Chung kết" color="success" sx={{ fontWeight: 700 }} />
                    }
                    if (cs === 'r1_shortlisted_for_review') {
                      return <Chip size="small" label="Shortlist" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                    }
                    if (cs === 'eliminated') {
                      return <Chip size="small" label="Đã loại" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
                    }
                    return (
                      <Typography variant="caption" color="text.secondary" fontStyle="italic">
                        Chưa công bố
                      </Typography>
                    )
                  })()}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default function ReportsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [tab, setTab] = useState<0 | 1>(0)
  const { data, isLoading, isError, error } = useGetReportsDashboardQuery()
  const [exportCsv] = useLazyExportScoresCsvQuery()
  const [exportTxt] = useLazyExportSummaryTxtQuery()
  const [exportPdf] = useLazyExportSummaryPdfQuery()
  const [adminCertTeam, { isFetching: certLoading }] = useLazyAdminDownloadTeamCertificateQuery()
  const [adminCertAll, { isFetching: certAllLoading }] = useLazyAdminDownloadAllCertificatesQuery()
  const [downloadingTeamId, setDownloadingTeamId] = useState<string | null>(null)

  // Toolbar dialog "Xuất chứng chỉ" — cho phép chọn case + (tùy chọn) 1 đội
  const [certDialogOpen, setCertDialogOpen] = useState(false)
  const [certDialogCaseId, setCertDialogCaseId] = useState('')
  const [certDialogTeamId, setCertDialogTeamId] = useState('') // '' = tất cả (ZIP)
  const [getDialogRanking, { data: dialogRanking, isFetching: dialogRankingLoading }] = useLazyGetRankingQuery()

  const handleOpenCertDialog = () => {
    setCertDialogCaseId(selectedCaseId || '')
    setCertDialogTeamId('')
    setCertDialogOpen(true)
  }

  // Khi đổi case trong dialog → fetch ranking để biết có những đội nào
  useEffect(() => {
    if (certDialogOpen && certDialogCaseId) {
      getDialogRanking({ case_id: certDialogCaseId, round_id: undefined as any })
      setCertDialogTeamId('')
    }
  }, [certDialogOpen, certDialogCaseId, getDialogRanking])

  const handleCertDialogDownload = async () => {
    if (!certDialogCaseId) return
    try {
      if (certDialogTeamId) {
        const team = dialogRanking?.ranking.find((r) => r.team_id === certDialogTeamId)
        await handleDownloadCertificateForCase(certDialogCaseId, certDialogTeamId, team?.team_name || certDialogTeamId)
      } else {
        await handleDownloadZipForCase(certDialogCaseId)
      }
      setCertDialogOpen(false)
    } catch {
      /* notify đã chạy bên trong helper */
    }
  }

  // Bỏ dấu tiếng Việt + chỉ giữ ASCII alnum/_-/đ→d cho tên file (cross-OS safe).
  // Vd: "Đội Luật Sư Xuất Sắc" → "Doi_Luat_Su_Xuat_Sac"
  const safeFilenameVi = (raw: string): string => {
    if (!raw) return 'file'
    const noDiacritics = raw
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // bỏ combining diacritical marks (U+0300..U+036F)
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
    const cleaned = noDiacritics
      .replace(/[^A-Za-z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80)
    return cleaned || 'file'
  }

  const handleDownloadCertificateForCase = async (caseId: string, teamId: string, teamName: string) => {
    setDownloadingTeamId(teamId)
    try {
      const blob = await adminCertTeam({ caseId, teamId }).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Certificate_${safeFilenameVi(teamName)}_${caseId}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Tải chứng chỉ thất bại', 'error')
      throw e
    } finally {
      setDownloadingTeamId(null)
    }
  }

  const handleDownloadZipForCase = async (caseId: string) => {
    try {
      const blob = await adminCertAll(caseId).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date().toISOString().replace(/[-:]/g, '').replace('T', '-').slice(0, 13)
      a.download = `E-Court_Certificates_${caseId}_${ts}.zip`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Tải ZIP chứng chỉ thất bại', 'error')
      throw e
    }
  }

  const handleDownloadCertificate = async (teamId: string, teamName: string) => {
    if (!selectedCaseId) return
    try {
      await handleDownloadCertificateForCase(selectedCaseId, teamId, teamName)
    } catch {
      /* notify đã chạy bên trong */
    }
  }

  const handleDownloadAllCertificates = async () => {
    if (!selectedCaseId) return
    try {
      await handleDownloadZipForCase(selectedCaseId)
    } catch {
      /* notify đã chạy bên trong */
    }
  }

  // Round/case filter for tab 1
  const { data: cases = [] } = useGetCasesQuery()
  const { data: rounds = [] } = useGetRoundsQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const [selectedCaseId, setSelectedCaseId] = useState('')
  const [selectedRoundId, setSelectedRoundId] = useState<string>('')   // '' = tất cả vòng
  // Spec PHẦN 9: detect dual_claimant_packets case → banner thông tin (R1 aggregate inline)
  const isDualClaimantCase = useMemo(() => {
    const c: any = cases.find((cc: any) => cc.id === selectedCaseId)
    return c?.r1_flow_version === 'dual_claimant_packets'
  }, [cases, selectedCaseId])
  const [getCompletion, { data: completion, isFetching: completionLoading }] =
    useLazyGetRoundCompletionQuery()
  const [getRanking, { data: ranking, isFetching: rankingLoading }] = useLazyGetRankingQuery()
  const [publishRound, { isLoading: publishing }] = usePublishRoundMutation()
  const [setR1Shortlist, { isLoading: settingShortlist }] = useSetR1ShortlistMutation()
  const [reopenScore, { isLoading: reopening }] = useReopenScoreMutation()

  // "Yêu cầu chấm lại" dialog state
  const [reopenDialog, setReopenDialog] = useState<{
    open: boolean
    scoreId: string | null
    judgeName: string
    teamName: string
    submissionType: string
  }>({ open: false, scoreId: null, judgeName: '', teamName: '', submissionType: '' })
  const [reopenReason, setReopenReason] = useState('')

  // Publish dialog state
  const [publishOpen, setPublishOpen] = useState(false)
  // Bug fix 2026-05-24: track rounds đã publish trong session để khoá nút "Công bố"
  // ngay sau lần bấm đầu (1-time publish/round). Persist across refresh tự suy ra
  // từ team.competition_status — xem `roundAlreadyPublished` ở dưới.
  const [publishedRoundIds, setPublishedRoundIds] = useState<Set<string>>(new Set())
  const [advancingTeams, setAdvancingTeams] = useState<string[]>([])
  const [publishTitle, setPublishTitle] = useState('')
  const [publishNote, setPublishNote] = useState('')
  const [publishStep, setPublishStep] = useState<'config' | 'preview'>('config')
  const [nextRoundLabel, setNextRoundLabel] = useState('Vòng Bán kết')
  const [scheduleBracketAt, setScheduleBracketAt] = useState('')
  const [scheduleRoleSheetDue, setScheduleRoleSheetDue] = useState('')
  const [scheduleHearingAt, setScheduleHearingAt] = useState('')

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  })
  const notify = (message: string, severity: typeof snackbar.severity) =>
    setSnackbar({ open: true, message, severity })

  const caseRounds = useMemo(
    () => rounds.filter((r: any) => r.case_id === selectedCaseId),
    [rounds, selectedCaseId]
  )

  const handleExportCsv = async () => {
    try {
      const blob = await exportCsv().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'scores_export.csv'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất CSV thất bại', 'error')
    }
  }

  const handleExportTxt = async () => {
    try {
      const text = await exportTxt().unwrap()
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'summary.txt'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất TXT thất bại', 'error')
    }
  }

  const handleExportPdf = async () => {
    try {
      const blob = await exportPdf().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date()
        .toISOString()
        .replace(/[-:]/g, '')
        .replace('T', '-')
        .slice(0, 13)
      a.download = `E-Court_Bao-cao-tong-ket_${ts}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất PDF thất bại', 'error')
    }
  }

  // UAT-TC #15: Tính điểm & Xếp hạng
  const handleCalculateRanking = async () => {
    if (!selectedCaseId) {
      notify('Vui lòng chọn cuộc thi', 'warning')
      return
    }
    try {
      // Kiểm tra giám khảo đã chấm xong (chỉ check khi đã chọn round cụ thể)
      if (selectedRoundId) {
        const c = await getCompletion({ case_id: selectedCaseId, round_id: selectedRoundId }).unwrap()
        if (!c.complete) {
          notify(
            `Mới có ${c.submitted_count}/${c.expected_total} phiếu được gửi — vẫn xếp hạng theo dữ liệu hiện có.`,
            'warning'
          )
        }
      }
      await getRanking({ case_id: selectedCaseId, round_id: selectedRoundId || null }).unwrap()
      notify('Đã tính điểm & cập nhật bảng xếp hạng.', 'success')
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Tính điểm thất bại', 'error')
    }
  }

  // UAT-TC #16 / Bán kết #11 / Chung kết #18: Công bố kết quả
  const handleOpenPublish = () => {
    if (!selectedCaseId || !selectedRoundId) {
      notify('Vui lòng chọn cuộc thi và vòng cụ thể trước khi công bố.', 'warning')
      return
    }
    if (!ranking || ranking.ranking.length === 0) {
      notify('Vui lòng tính điểm xếp hạng trước.', 'warning')
      return
    }
    const round = caseRounds.find((r: any) => r.id === selectedRoundId)
    const roundType = (round as any)?.round_type
    // Auto-pick top N theo round_type — logic ở utils/r2.ts để test được.
    const pick = publishAutoPick(roundType)
    const topN = pick?.topN ?? 0
    const nextLabel = pick?.nextLabel ?? 'Vòng tiếp theo'
    const autoPick = topN > 0 ? ranking.ranking.slice(0, topN).map((r) => r.team_id) : []
    setAdvancingTeams(autoPick)
    setNextRoundLabel(nextLabel)
    setPublishTitle(`Kết quả ${round?.name || selectedRoundId}`)
    setPublishNote('')
    setScheduleBracketAt('')
    setScheduleRoleSheetDue('')
    setScheduleHearingAt('')
    setPublishStep('config')
    setPublishOpen(true)
  }

  const handleConfirmPublish = async () => {
    try {
      const schedule: Record<string, string> = {}
      if (scheduleBracketAt) schedule.bracket_at = scheduleBracketAt
      if (scheduleRoleSheetDue) schedule.role_sheet_due = scheduleRoleSheetDue
      if (scheduleHearingAt) schedule.hearing_at = scheduleHearingAt
      const result = await publishRound({
        case_id: selectedCaseId,
        round_id: selectedRoundId,
        advancing_team_ids: advancingTeams,
        title: publishTitle,
        note: publishNote,
        next_round_label: nextRoundLabel,
        next_round_schedule: Object.keys(schedule).length ? schedule : undefined,
      }).unwrap()
      notify(
        `Đã công bố kết quả. Đã gửi thông báo + email cho ${result.notified_teams} đội.`,
        'success'
      )
      // Bug fix 2026-05-24: khoá nút "Công bố" cho round vừa publish ngay session
      // này (1-time per round). Sau refresh, derived state qua competition_status sẽ
      // giữ nguyên trạng thái khoá.
      setPublishedRoundIds((prev) => {
        const next = new Set(prev)
        next.add(selectedRoundId)
        return next
      })
      setPublishOpen(false)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Công bố kết quả thất bại', 'error')
    }
  }

  // Bug fix 2026-05-24: round được coi là "đã công bố" nếu (a) session vừa publish
  // hoặc (b) có team nào trong case đã có competition_status (set bởi BE khi
  // publish_round_results) — durable across page refresh.
  const roundAlreadyPublished = useMemo(() => {
    if (!selectedRoundId || !selectedCaseId) return false
    if (publishedRoundIds.has(selectedRoundId)) return true
    const round: any = caseRounds.find((r: any) => r.id === selectedRoundId)
    const rt = round?.round_type
    const targetStatuses =
      rt === 'memorial' ? ['advanced_to_semifinal', 'eliminated']
      : rt === 'semifinal' || rt === 'online_hearing' ? ['advanced_to_final']
      : []
    if (targetStatuses.length === 0) return false
    const currentCase: any = cases.find((c) => c.id === selectedCaseId)
    const assigned: string[] = currentCase?.assigned_team_ids || []
    return assigned.some((tid) => {
      const t: any = teams.find((x) => x.id === tid)
      return t && targetStatuses.includes(t.competition_status)
    })
  }, [selectedRoundId, selectedCaseId, publishedRoundIds, caseRounds, cases, teams])

  // Tied teams that need tie-break
  const tiedRows = (ranking?.ranking || []).filter((r) => r.tied_with_neighbor)
  const advancingRows = (ranking?.ranking || []).filter((r) =>
    advancingTeams.includes(r.team_id),
  )
  const eliminatedRows = (ranking?.ranking || []).filter(
    (r) => !advancingTeams.includes(r.team_id),
  )
  const canManageResults = useHasRole(['secretary'])
  const isSupport = useHasRole(['support'])
  const canExportReports = canManageResults || isSupport

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Báo cáo và Phân tích"
        subtitle="Tổng quan điểm số, xếp hạng theo vòng, công bố kết quả."
        icon={<Assessment />}
        mb={3}
        actions={
          canExportReports ? (
          <>
            <Button variant="outlined" color="primary" startIcon={<Download />} onClick={handleExportCsv}>
              Xuất CSV điểm
            </Button>
            <Button variant="contained" color="primary" startIcon={<Download />} onClick={handleExportPdf}>
              Xuất PDF
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<EmojiEvents />}
              onClick={handleOpenCertDialog}
              sx={{
                background: gradientBrand,
                '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
              }}
            >
              Xuất chứng chỉ
            </Button>
          </>
          ) : null
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Tổng quan" />
        <Tab label="Vòng & Xếp hạng" />
      </Tabs>

      {tab === 0 && (
        <>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {(error as any)?.data?.detail || 'Không tải được thống kê.'}
            </Alert>
          )}
          {data && (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Cuộc thi
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_cases}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><Folder /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tổng đội
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_teams}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}><People /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Phiên courtroom
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_courtrooms}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}><VideoCall /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Điểm TB
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">
                            {data.average_total_score != null ? data.average_total_score : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Phiếu đã gửi: {data.submitted_scores_count}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}><TrendingUp /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3 })}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Bảng xếp hạng tổng (theo điểm TB phiếu đã gửi)
                </Typography>
                <TableContainer sx={(theme) => getTablePaperSx(theme)}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Số thứ tự</TableCell>
                        <TableCell>Đội</TableCell>
                        <TableCell align="right">Điểm TB</TableCell>
                        <TableCell align="right">Số phiếu</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.leaderboard || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography color="text.secondary">Chưa có dữ liệu xếp hạng.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.leaderboard.map((row, idx) => (
                          <TableRow key={row.team_id} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row.team_name}</TableCell>
                            <TableCell align="right">
                              <Chip label={row.avg_score} size="small" color="primary" />
                            </TableCell>
                            <TableCell align="right">{row.score_count}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {/* Spec PHẦN 9 — banner thông tin cuộc thi dual_claimant_packets.
              Sau khi xoá R1Scorecard (Option A), ranking R1 /200 được tổng hợp
              ngay tại tab này (group by submission_index 1+2). Không còn route
              riêng /r1-review. */}
          {isDualClaimantCase && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Cuộc thi này dùng flow R1 mới (<code>dual_claimant_packets</code>)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Mỗi đội có 2 hồ sơ /100 (đại diện nguyên đơn + bị đơn). Bảng dưới
                tổng hợp main_score /200 (sum 2 hồ sơ) + tie-break Video /30 khi
                cần.
              </Typography>
            </Alert>
          )}
          {/* Filter: Cuộc thi + Vòng */}
          <Paper
            elevation={0}
            sx={(t) => ({ ...getFilterPaperSx(t), display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' })}
          >
            <FormControl sx={{ minWidth: 240, flex: 1 }} size="small">
              <InputLabel>Cuộc thi</InputLabel>
              <Select
                value={selectedCaseId}
                label="Cuộc thi"
                onChange={(e) => {
                  setSelectedCaseId(e.target.value)
                  setSelectedRoundId('')
                }}
              >
                {cases.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }} size="small" disabled={!selectedCaseId}>
              <InputLabel>Vòng</InputLabel>
              <Select
                value={selectedRoundId}
                label="Vòng"
                onChange={(e) => setSelectedRoundId(e.target.value)}
              >
                <MenuItem value=""><em>Tổng hợp tất cả vòng</em></MenuItem>
                {caseRounds.map((r: any) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name} ({r.round_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {canManageResults && (
              <>
                <Button
                  variant="contained"
                  startIcon={<Calculate />}
                  onClick={handleCalculateRanking}
                  disabled={!selectedCaseId || rankingLoading || completionLoading}
                  sx={{ background: gradientBrand, textTransform: 'none' }}
                >
                  {rankingLoading || completionLoading ? 'Đang tính...' : 'Tính điểm & Xếp hạng'}
                </Button>
                <Tooltip
                  title={
                    roundAlreadyPublished
                      ? 'Vòng này đã công bố kết quả. Không thể công bố lại để tránh ghi đè trạng thái đội thi.'
                      : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Campaign />}
                      onClick={handleOpenPublish}
                      disabled={!selectedCaseId || !selectedRoundId || !ranking || roundAlreadyPublished}
                      sx={{ textTransform: 'none' }}
                    >
                      {roundAlreadyPublished ? 'Đã công bố' : 'Công bố kết quả'}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Paper>

          {/* Trạng thái chấm điểm của giám khảo.
              C1 (audit 2026-05-22): ẨN cho R1 (memorial) vì R1 dual flow admin
              chấm trực tiếp, không có khái niệm "phiếu GK" như R2/R3.
              Chỉ show cho semifinal/final. */}
          {selectedRoundId && completion && (() => {
            const round = caseRounds.find((r: any) => r.id === selectedRoundId)
            const rt = (round as any)?.round_type
            return rt === 'semifinal' || rt === 'final' || rt === 'online_hearing'
          })() && (
            <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Tiến độ chấm điểm vòng
                </Typography>
                <Chip
                  icon={completion.complete ? <CheckCircle /> : <Cancel />}
                  label={
                    completion.complete
                      ? `Hoàn thành ${completion.submitted_count}/${completion.expected_total}`
                      : `Đang chấm ${completion.submitted_count}/${completion.expected_total}`
                  }
                  color={completion.complete ? 'success' : 'warning'}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Giám khảo</strong></TableCell>
                      <TableCell align="center"><strong>Tiến độ</strong></TableCell>
                      <TableCell align="right"><strong>Đã chấm</strong></TableCell>
                      <TableCell align="right"><strong>Cần chấm</strong></TableCell>
                      <TableCell align="right"><strong>Trạng thái</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completion.judges.map((j) => {
                      const pct = j.expected > 0 ? (j.done / j.expected) * 100 : 0
                      return (
                        <TableRow key={j.judge_id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {j.judge_name || 'Chưa xác định'}
                            </Typography>
                            {j.judge_email && (
                              <Typography variant="caption" color="text.secondary">
                                {j.judge_email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 160 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 6,
                                  borderRadius: 1,
                                  bgcolor: 'grey.200',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.min(100, pct)}%`,
                                    height: '100%',
                                    bgcolor: j.complete ? 'success.main' : 'warning.main',
                                    transition: 'width 0.2s',
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" fontWeight={600} sx={{ minWidth: 36 }}>
                                {pct.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {j.done}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {j.expected}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={j.complete ? 'Hoàn tất' : 'Đang chấm'}
                              color={j.complete ? 'success' : 'warning'}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Bảng xếp hạng theo vòng */}
          {ranking && (
            <Paper elevation={0} sx={(t) => getContentPaperSx(t)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Xếp hạng — {selectedRoundId ? 'theo vòng đã chọn' : 'tổng hợp các vòng'}
                </Typography>
                {ranking.tie_break_applied && (
                  <Alert severity="info" sx={{ width: '100%', mt: 1 }} icon={<Warning fontSize="small" />}>
                    <b>Có đội đồng điểm bài thi chính</b> — đã áp dụng tie-break theo
                    Điều 10.3: <b>so tổng điểm Video giới thiệu</b> (3 tiêu chí
                    Sáng tạo / Trình bày / Chuyên nghiệp, max 30đ). Tổng điểm video
                    được tính từ <b>AVG các GK chấm phiếu Video Sơ loại</b>; fallback
                    về điểm BTC chấm tay nếu chưa có GK nào chấm.
                  </Alert>
                )}
                {canExportReports && (
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadAllCertificates}
                    disabled={!selectedCaseId || certAllLoading || ranking.ranking.length === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    {certAllLoading ? 'Đang tạo ZIP...' : 'Tải tất cả chứng chỉ (ZIP)'}
                  </Button>
                )}
                {/* C3-a (audit 2026-05-22): R1 shortlist — chỉ hiện cho R1 dual.
                    Admin chọn top N (default 15) từ ranking → set status
                    `r1_shortlisted_for_review`. Sau đó BTC review → chốt
                    top 4 qua publishRound. */}
                {canManageResults && ranking.flow === 'dual_claimant_packets' && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<EmojiEvents />}
                    disabled={
                      settingShortlist || ranking.ranking.length === 0 || !selectedCaseId
                    }
                    onClick={async () => {
                      const currentCase: any = cases.find((c) => c.id === selectedCaseId)
                      const defaultSize = currentCase?.r1_shortlist_size ?? 15
                      const input = window.prompt(
                        `Nhập số đội vào shortlist R1 (top N). Default: ${defaultSize}.\n` +
                          `Các đội còn lại sẽ chuyển sang trạng thái 'eliminated'.`,
                        String(defaultSize),
                      )
                      if (!input) return
                      const n = parseInt(input, 10)
                      if (isNaN(n) || n <= 0 || n > ranking.ranking.length) {
                        notify(`Số đội không hợp lệ (1-${ranking.ranking.length})`, 'warning')
                        return
                      }
                      const eligible = ranking.ranking
                        .filter((r) => !r.incomplete)
                        .sort((a, b) => (a.rank || 999) - (b.rank || 999))
                      const topN = eligible.slice(0, n).map((r) => r.team_id)
                      const ok = window.confirm(
                        `Lập shortlist ${topN.length} đội đầu (theo điểm chính).\n` +
                          `${ranking.ranking.length - topN.length} đội còn lại → 'eliminated'.\n\n` +
                          `Tiếp tục?`,
                      )
                      if (!ok) return
                      try {
                        const res = await setR1Shortlist({
                          case_id: selectedCaseId,
                          shortlisted_team_ids: topN,
                        }).unwrap()
                        notify(
                          `Đã shortlist ${res.shortlisted_count} đội · ` +
                            `${res.eliminated_count} đội bị eliminate.`,
                          'success',
                        )
                      } catch (e: any) {
                        notify(e?.data?.detail || e?.message || 'Lập shortlist thất bại', 'error')
                      }
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    {settingShortlist ? 'Đang lọc...' : 'Lọc top 15'}
                  </Button>
                )}
              </Box>
              {ranking.flow === 'dual_claimant_packets' ? (
                <R1DualRankingTable ranking={ranking} teams={teams as any} />
              ) : (
              <TableContainer sx={(t) => getTablePaperSx(t)}>
                <Table size="small" sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 60 }}>Số thứ tự</TableCell>
                      <TableCell sx={{ minWidth: 200 }}><strong>Đội</strong></TableCell>
                      <TableCell align="right" sx={{ minWidth: 110 }}>
                        <strong>Điểm chính</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Memorial + Chứng cứ (Đ.10.2, max 100)
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 110 }}>
                        <strong>Tổng cộng</strong>
                        <Typography variant="caption" display="block" color="text.secondary">cộng dồn tất cả phiếu</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 90 }}>
                        <strong>Số GK</strong>
                      </TableCell>
                      <TableCell sx={{ minWidth: 240 }}>
                        <strong>Điểm video (Đ.10.3)</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          GK chấm — tie-break khi điểm chính bằng nhau
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 280 }}><strong>Chi tiết phiếu chấm</strong></TableCell>
                      {canExportReports && (
                        <TableCell align="right" sx={{ minWidth: 130 }}><strong>Chứng chỉ</strong></TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ranking.ranking.map((r) => (
                      <TableRow key={r.team_id} hover sx={{ verticalAlign: 'top' }}>
                        <TableCell>
                          <Chip
                            label={`#${r.rank}`}
                            size="small"
                            color={r.rank === 1 ? 'warning' : r.rank <= 3 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.team_name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color={r.average_score > 0 ? 'primary.main' : 'text.secondary'}>
                            {r.average_score?.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{r.total_score?.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>{r.judge_count}</Typography>
                          {r.score_count !== undefined && r.score_count !== r.judge_count && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ({r.score_count} phiếu)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const judgeAvg = r.video_avg_score ?? 0
                            const judgeCount = r.video_judge_count ?? 0
                            const btcTotal = r.video_tiebreak_total ?? 0
                            const source = r.video_source || 'none'
                            const effective = r.effective_video_score ?? 0

                            if (source === 'none') {
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                  <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                    Chưa có điểm video
                                  </Typography>
                                  {r.tied_with_neighbor && (
                                    <Chip
                                      size="small"
                                      label="⚖ Đồng điểm — cần GK chấm video"
                                      color="warning"
                                      sx={{ height: 18, fontSize: '0.66rem', alignSelf: 'flex-start' }}
                                    />
                                  )}
                                </Box>
                              )
                            }
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                <Chip
                                  size="small"
                                  label={`Σ ${effective.toFixed(2)} / 30`}
                                  color={r.tied_with_neighbor ? 'warning' : 'primary'}
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, alignSelf: 'flex-start' }}
                                />
                                {source === 'judge_avg' && (
                                  <Typography variant="caption" color="text.secondary">
                                    {judgeCount} GK chấm · TB
                                  </Typography>
                                )}
                                {source === 'btc_manual' && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <Typography variant="caption" color="warning.main" fontStyle="italic">
                                      BTC chấm tay (legacy) — {btcTotal.toFixed(1)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                                      <Chip size="small" label={`ST ${(r.video_tiebreak?.creativity ?? 0).toFixed(1)}`} sx={{ height: 16, fontSize: '0.6rem' }} />
                                      <Chip size="small" label={`TB ${(r.video_tiebreak?.presentation ?? 0).toFixed(1)}`} sx={{ height: 16, fontSize: '0.6rem' }} />
                                      <Chip size="small" label={`CN ${(r.video_tiebreak?.professionalism ?? 0).toFixed(1)}`} sx={{ height: 16, fontSize: '0.6rem' }} />
                                    </Box>
                                  </Box>
                                )}
                                {source === 'judge_avg' && judgeAvg !== effective && (
                                  <Typography variant="caption" color="text.secondary">
                                    AVG GK: {judgeAvg.toFixed(2)}
                                  </Typography>
                                )}
                              </Box>
                            )
                          })()}
                        </TableCell>
                        <TableCell>
                          {(!r.judge_details || r.judge_details.length === 0) ? (
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                              Chưa có giám khảo nào gửi điểm chính thức
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {r.judge_details.map((jd) => {
                                const subLabels: Record<string, string> = {
                                  memorial: 'Đơn KK',
                                  evidence: 'Chứng cứ',
                                  transcript: 'Tranh tụng',
                                }
                                return (
                                  <Box key={jd.judge_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 80 }}>
                                      {jd.judge_name}:
                                    </Typography>
                                    {jd.submissions.map((sub, i) => (
                                      <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                        <Chip
                                          size="small"
                                          label={`${subLabels[sub.submission_type] || sub.submission_type || '?'} ${sub.score.toFixed(1)}đ`}
                                          sx={{ height: 18, fontSize: '0.68rem' }}
                                          variant="outlined"
                                        />
                                        {canExportReports && sub.score_id && (
                                          <Button
                                            size="small"
                                            variant="text"
                                            color="warning"
                                            startIcon={<Replay sx={{ fontSize: 12 }} />}
                                            sx={{
                                              minWidth: 0,
                                              p: '0 4px',
                                              fontSize: '0.6rem',
                                              textTransform: 'none',
                                              lineHeight: 1,
                                            }}
                                            onClick={() => {
                                              setReopenDialog({
                                                open: true,
                                                scoreId: sub.score_id!,
                                                judgeName: jd.judge_name,
                                                teamName: r.team_name,
                                                submissionType: subLabels[sub.submission_type] || sub.submission_type || '?',
                                              })
                                              setReopenReason('')
                                            }}
                                            title="Yêu cầu giám khảo chấm lại phiếu này"
                                          >
                                            Chấm lại
                                          </Button>
                                        )}
                                      </Box>
                                    ))}
                                    <Chip
                                      size="small"
                                      label={`= ${jd.judge_total.toFixed(2)}đ`}
                                      color="primary"
                                      sx={{ height: 18, fontSize: '0.68rem', fontWeight: 700 }}
                                    />
                                  </Box>
                                )
                              })}
                            </Box>
                          )}
                        </TableCell>
                        {canExportReports && (
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Download fontSize="small" />}
                              onClick={() => handleDownloadCertificate(r.team_id, r.team_name)}
                              disabled={(certLoading && downloadingTeamId === r.team_id)}
                              sx={{ textTransform: 'none' }}
                            >
                              {certLoading && downloadingTeamId === r.team_id ? 'Đang tải...' : 'Tải PDF'}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Paper>
          )}
        </>
      )}

      {/* Dialog: Công bố kết quả — 2 step stepper (config → preview) */}
      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment color="primary" />
            <span>Công bố kết quả vòng</span>
            <Chip
              size="small"
              label={publishStep === 'config' ? 'Bước 1/2 — Cấu hình' : 'Bước 2/2 — Xem trước email'}
              color={publishStep === 'config' ? 'primary' : 'success'}
              sx={{ ml: 1 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {publishStep === 'config' && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Lưu ý:</b> Sau khi công bố, hệ thống sẽ gửi email + thông báo cho TẤT CẢ đội.
                Đội qua vòng nhận Mẫu A (chúc mừng kèm hạng/điểm + lịch trình),
                đội còn lại nhận Mẫu B (cảm ơn — KHÔNG nhấn mạnh hạng).
              </Alert>

              {tiedRows.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
                  <b>Phát hiện {tiedRows.length} đội đồng điểm</b> — đã áp dụng tie-break
                  Điều 10.3 (Video clip). Hãy đảm bảo BTC đã chấm điểm tie-break trong TeamsPage
                  trước khi công bố.
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  label="Tiêu đề công bố"
                  value={publishTitle}
                  onChange={(e) => setPublishTitle(e.target.value)}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Đội qua vòng (Top {advancingTeams.length})</InputLabel>
                  <Select
                    multiple
                    value={advancingTeams}
                    onChange={(e) => setAdvancingTeams(e.target.value as string[])}
                    label={`Đội qua vòng (Top ${advancingTeams.length})`}
                    renderValue={(sel) =>
                      `${(sel as string[]).length} đội: ` +
                      (sel as string[]).map((id) => teams.find((t) => t.id === id)?.name || id).join(', ')
                    }
                  >
                    {(ranking?.ranking || []).map((r) => (
                      <MenuItem key={r.team_id} value={r.team_id}>
                        <Checkbox checked={advancingTeams.indexOf(r.team_id) > -1} />
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>#{r.rank} — {r.team_name}</span>
                              {r.tied_with_neighbor && (
                                <Chip size="small" label="⚖ tie-break" color="warning" />
                              )}
                            </Box>
                          }
                          secondary={
                            `Điểm TB: ${r.average_score?.toFixed(2)}` +
                            (r.video_tiebreak_total
                              ? ` · Video tiebreak: ${r.video_tiebreak_total.toFixed(1)}/30`
                              : '')
                          }
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider>Lịch trình vòng tiếp theo (sẽ insert vào email Mẫu A)</Divider>

                <TextField
                  label="Tên vòng tiếp theo"
                  value={nextRoundLabel}
                  onChange={(e) => setNextRoundLabel(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
                  <TextField
                    label="Bốc thăm cặp đấu"
                    type="datetime-local"
                    value={scheduleBracketAt}
                    onChange={(e) => setScheduleBracketAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="Hạn nộp Bảng phân vai"
                    type="datetime-local"
                    value={scheduleRoleSheetDue}
                    onChange={(e) => setScheduleRoleSheetDue(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label={`Phiên ${nextRoundLabel}`}
                    type="datetime-local"
                    value={scheduleHearingAt}
                    onChange={(e) => setScheduleHearingAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>

                <TextField
                  label="Ghi chú thêm (tuỳ chọn)"
                  value={publishNote}
                  onChange={(e) => setPublishNote(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>
            </>
          )}

          {publishStep === 'preview' && (
            <Stack spacing={2}>
              <Alert severity="info">
                <b>Xem trước nội dung sẽ gửi:</b> {advancingRows.length} đội nhận Mẫu A,{' '}
                {eliminatedRows.length} đội nhận Mẫu B.
              </Alert>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  ✉️ Mẫu A — gửi cho {advancingRows.length} đội đi tiếp
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>
                  {`Tiêu đề: [Gender Justice Moot Court] Chúc mừng Đội <Tên đội> vào ${nextRoundLabel}!\n\nKính gửi Đội trưởng và các thành viên Đội <Tên đội>,\n\nBan Tổ chức trân trọng chúc mừng Đội <Tên đội> đã xuất sắc đạt\nhạng #<X> với tổng điểm <YY.Y> và chính thức được lựa chọn vào ${nextRoundLabel}.\n\n📅 Lịch trình tiếp theo:\n${scheduleBracketAt ? `   • Bốc thăm cặp đấu: ${new Date(scheduleBracketAt).toLocaleString('vi-VN')}\n` : ''}${scheduleRoleSheetDue ? `   • Nộp Bảng phân vai: trước ${new Date(scheduleRoleSheetDue).toLocaleString('vi-VN')}\n` : ''}${scheduleHearingAt ? `   • Phiên ${nextRoundLabel}: ${new Date(scheduleHearingAt).toLocaleString('vi-VN')}\n` : ''}${!scheduleBracketAt && !scheduleRoleSheetDue && !scheduleHearingAt ? '   • Lịch chi tiết sẽ được BTC công bố.\n' : ''}\nTrân trọng,\nBan Tổ chức Cuộc thi`}
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Đội nhận:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {advancingRows.map((r) => (
                      <Chip
                        key={r.team_id}
                        size="small"
                        label={`#${r.rank} ${r.team_name} (${r.average_score?.toFixed(2)})`}
                        color="success"
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom>
                  ✉️ Mẫu B — gửi cho {eliminatedRows.length} đội còn lại
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>
                  {`Tiêu đề: [Gender Justice Moot Court] Cảm ơn Đội <Tên đội> đã tham gia Cuộc thi\n\nKính gửi Đội trưởng và các thành viên Đội <Tên đội>,\n\nBan Tổ chức Cuộc thi chân thành cảm ơn Đội <Tên đội> đã tham gia\nvà hoàn thành vòng thi. ...\n\n(KHÔNG nhấn mạnh thứ hạng — đội có thể tự xem điểm trong workspace)\n\nTrân trọng,\nBan Tổ chức Cuộc thi`}
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Đội nhận:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {eliminatedRows.map((r) => (
                      <Chip
                        key={r.team_id}
                        size="small"
                        label={`#${r.rank} ${r.team_name}`}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Huỷ</Button>
          {publishStep === 'config' ? (
            <Button
              onClick={() => setPublishStep('preview')}
              variant="outlined"
              disabled={advancingTeams.length === 0}
            >
              Xem trước email →
            </Button>
          ) : (
            <>
              <Button onClick={() => setPublishStep('config')}>← Quay lại cấu hình</Button>
              <Button
                onClick={handleConfirmPublish}
                variant="contained"
                color="success"
                disabled={publishing || advancingTeams.length === 0}
                startIcon={<CheckCircle />}
              >
                {publishing ? 'Đang công bố...' : 'Xác nhận công bố & gửi email'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog: Xuất chứng chỉ — chọn case + (tùy chọn) 1 đội cụ thể, hoặc tải toàn bộ ZIP */}
      <Dialog open={certDialogOpen} onClose={() => setCertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Xuất chứng chỉ điện tử</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              Chứng chỉ ICFGL × UEL — chọn cuộc thi và (tùy chọn) 1 đội cụ thể.
              Bỏ trống đội = tải toàn bộ ZIP cho cả cuộc thi.
            </Alert>
            <FormControl fullWidth size="small">
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                value={certDialogCaseId}
                label="Cuộc thi *"
                onChange={(e) => setCertDialogCaseId(e.target.value)}
              >
                <MenuItem value="">— Chọn cuộc thi —</MenuItem>
                {cases.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title || '— Chưa đặt tên —'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!certDialogCaseId || dialogRankingLoading}>
              <InputLabel>Đội thi (để trống = tất cả ZIP)</InputLabel>
              <Select
                value={certDialogTeamId}
                label="Đội thi (để trống = tất cả ZIP)"
                onChange={(e) => setCertDialogTeamId(e.target.value)}
              >
                <MenuItem value="">— Tất cả đội (.zip) —</MenuItem>
                {(dialogRanking?.ranking || []).map((r) => (
                  <MenuItem key={r.team_id} value={r.team_id}>
                    #{r.rank} · {r.team_name} ({r.average_score?.toFixed(2)} đ)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {certDialogCaseId && !dialogRankingLoading && (dialogRanking?.ranking?.length || 0) === 0 && (
              <Alert severity="warning">
                Cuộc thi này chưa có bảng xếp hạng công bố — chưa thể xuất chứng chỉ.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCertDialogOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleCertDialogDownload}
            disabled={
              !certDialogCaseId ||
              dialogRankingLoading ||
              (dialogRanking?.ranking?.length || 0) === 0 ||
              certLoading ||
              certAllLoading
            }
            sx={{
              background: gradientBrand,
              '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
            }}
          >
            {certLoading || certAllLoading
              ? 'Đang tạo PDF...'
              : certDialogTeamId
                ? 'Tải PDF của đội'
                : 'Tải ZIP toàn bộ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Yêu cầu chấm lại — Admin reopen 1 phiếu cụ thể */}
      <Dialog
        open={reopenDialog.open}
        onClose={() => setReopenDialog((s) => ({ ...s, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Replay color="warning" />
            <span>Yêu cầu chấm lại phiếu điểm</span>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Phiếu của <b>{reopenDialog.judgeName}</b> chấm cho đội{' '}
              <b>{reopenDialog.teamName}</b> ({reopenDialog.submissionType}) sẽ chuyển
              từ <code>submitted</code> về <code>draft</code>, GK sẽ nhận thông báo và
              phải gửi lại. Hành động được audit log (<code>score.reopen</code>).
            </Alert>
            <TextField
              required
              autoFocus
              label="Lý do yêu cầu chấm lại"
              placeholder="Ví dụ: phát hiện nhập sai 1 tiêu chí; GK báo lỗi mạng giữa phiên..."
              multiline
              minRows={3}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              error={!reopenReason.trim()}
              helperText={!reopenReason.trim() ? 'Bắt buộc nhập lý do để lưu audit log.' : ''}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenDialog((s) => ({ ...s, open: false }))}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!reopenReason.trim() || reopening}
            onClick={async () => {
              if (!reopenDialog.scoreId) return
              try {
                await reopenScore({
                  id: reopenDialog.scoreId,
                  reason: reopenReason.trim(),
                }).unwrap()
                notify('Đã gửi yêu cầu chấm lại — GK nhận thông báo.', 'success')
                setReopenDialog({ open: false, scoreId: null, judgeName: '', teamName: '', submissionType: '' })
                // Refresh ranking + completion
                if (selectedCaseId) {
                  getRanking({ case_id: selectedCaseId, round_id: selectedRoundId || null })
                  if (selectedRoundId) {
                    getCompletion({ case_id: selectedCaseId, round_id: selectedRoundId })
                  }
                }
              } catch (e: any) {
                notify(e?.data?.detail || 'Yêu cầu chấm lại thất bại', 'error')
              }
            }}
          >
            {reopening ? 'Đang gửi...' : 'Xác nhận yêu cầu chấm lại'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
